import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useInfiniteScroll } from '@/shared/hooks/useInfiniteScroll';
import { GET_NOTIFICATIONS } from '@/shared/queries';
import { MARK_NOTIFICATION_AS_READ } from '@/shared/mutations';
import SentryService from '@/services/sentry/Sentry.service';
import type {
  Notification,
  NotificationListResponse,
  QueryNotificationsArgs,
  MutationMarkNotificationAsReadArgs,
  MutationResult,
} from '@/shared/types/generated';

interface UseNotificationListOptions {
  pageSize?: number;
  enabled?: boolean;
  onError?: (error: Error) => void;
  onNotificationMarkedAsRead?: (notification: Notification) => void;
}

interface UseNotificationListResult {
  notifications: Notification[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  total: number;
  refetch: () => void;
  loadMore: () => void;
  refresh: () => void;
  markAsRead: (notificationId: number) => Promise<void>; // Throws on error - handle gracefully
  markingAsRead: boolean;
}

export function useNotificationList({
  pageSize = 20,
  enabled = true,
  onError,
  onNotificationMarkedAsRead,
}: UseNotificationListOptions = {}): UseNotificationListResult {
  // Infinite scroll for notifications data
  const {
    data: notifications,
    loading,
    loadingMore,
    hasMore,
    error,
    total,
    refetch,
    loadMore,
    refresh,
  } = useInfiniteScroll<
    { notifications: NotificationListResponse },
    QueryNotificationsArgs,
    Notification
  >({
    query: GET_NOTIFICATIONS,
    dataKey: 'notifications',
    pageSize,
    enabled,
    onError: (error) => {
      SentryService.captureError('Error fetching notifications', error);
      onError?.(error);
    },
  });

  // Mutation for marking notification as read
  const [markNotificationAsRead, { loading: markingAsRead }] = useMutation<
    MutationResult<'markNotificationAsRead'>,
    MutationMarkNotificationAsReadArgs
  >(MARK_NOTIFICATION_AS_READ, {
    onError: (error) => {
      SentryService.captureError('Error marking notification as read', error);
      onError?.(error);
    },
  });

  // Mark a notification as read
  const markAsRead = useCallback(
    async (notificationId: number): Promise<void> => {
      try {
        const { data } = await markNotificationAsRead({
          variables: {
            input: { notificationId },
          },
          update: (cache, { data }) => {
            if (data?.markNotificationAsRead) {
              // Update the notification in the cache to mark it as read
              cache.modify({
                id: cache.identify(data.markNotificationAsRead),
                fields: {
                  isRead: () => true,
                  openedAt: () => data.markNotificationAsRead?.openedAt,
                },
              });
            }
          },
        });

        if (data?.markNotificationAsRead) {
          onNotificationMarkedAsRead?.(data.markNotificationAsRead);
        }
      } catch (error) {
        SentryService.captureError(
          'Failed to mark notification as read',
          error,
        );
        throw error;
      }
    },
    [markNotificationAsRead, onNotificationMarkedAsRead],
  );

  return {
    notifications,
    loading,
    loadingMore,
    hasMore,
    error,
    total,
    refetch,
    loadMore,
    refresh,
    markAsRead,
    markingAsRead,
  };
}
